import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { Toaster, toast } from "react-hot-toast";
import {
    MailOutlined,
    LockOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    LoginOutlined,
    SafetyCertificateOutlined,
    ExclamationCircleOutlined,
    AuditOutlined,
} from "@ant-design/icons";
import { NEW_API_SERVER } from "src/components/Auth/AuthGuard";
import { usePermissions } from "src/auth/permissions/PermissionProvider";

const LoginPage = () => {
    const navigate = useNavigate();
    const { refreshPermissions } = usePermissions();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validate = () => {
        const next = {};
        if (!email.trim()) next.email = "ایمیل را وارد کنید";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "فرمت ایمیل نامعتبر است";
        if (!password) next.password = "رمز عبور را وارد کنید";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        const toastId = toast.loading("در حال بررسی اطلاعات ورود...");
        try {
            const res = await axios.post(`${NEW_API_SERVER()}login/access-token`, {
                email: email.trim(),
                password,
            });

            const { access_token, token_type } = res.data;

            Cookies.set("Authorization", `${token_type} ${access_token}`, {
                expires: 7,
                sameSite: "Lax",
                secure: window.location.protocol === "https:",
                path: "/",
            });

            const loadedPermissions = await refreshPermissions();

            if (loadedPermissions) {
                toast.success("ورود با موفقیت انجام شد ✓", { id: toastId });
            } else {
                toast.error("ورود انجام شد، اما دریافت سطح دسترسی ناموفق بود", {
                    id: toastId,
                });
            }

            navigate("/dashboard-page");
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || "ایمیل یا رمز عبور نادرست است", { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="lg-root">
            <Toaster
                position="top-center"
                toastOptions={{
                    style: {
                        direction: "rtl",
                        fontWeight: 600,
                        fontSize: "13.5px",
                        background: "#FBF9F4",
                        color: "#1C2321",
                        border: "1px solid #E4DFD1",
                        boxShadow: "0 12px 30px rgba(14,26,22,0.18)",
                        borderRadius: "12px",
                        padding: "12px 16px",
                    },
                    success: {
                        iconTheme: { primary: "#1F6F63", secondary: "#FBF9F4" },
                        style: { border: "1px solid #1F6F63" },
                    },
                    error: {
                        iconTheme: { primary: "#A6453B", secondary: "#FBF9F4" },
                        style: { border: "1px solid #A6453B" },
                    },
                    loading: {
                        iconTheme: { primary: "#B08D46", secondary: "#FBF9F4" },
                        style: { border: "1px solid #B08D46" },
                    },
                }}
            />

            <div className="lg-card lg-anim-rise">
                <div className="lg-header">
                    <div className="lg-seal" aria-hidden="true">
                        <svg viewBox="0 0 120 120" className="lg-seal-svg">
                            <defs>
                                <linearGradient id="lgGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#D4B876" />
                                    <stop offset="100%" stopColor="#8B6A2E" />
                                </linearGradient>
                            </defs>
                            <g className="lg-seal-ticks">
                                {Array.from({ length: 16 }).map((_, i) => (
                                    <line
                                        key={i}
                                        x1="60"
                                        y1="6"
                                        x2="60"
                                        y2="14"
                                        stroke="#D4B876"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        opacity="0.55"
                                        transform={`rotate(${i * 22.5} 60 60)`}
                                    />
                                ))}
                            </g>
                            <circle cx="60" cy="60" r="42" fill="none" stroke="url(#lgGoldGrad)" strokeWidth="3" />
                            <circle cx="60" cy="60" r="34" fill="#142722" stroke="#D4B876" strokeWidth="1.5" />
                            <path
                                d="M45 61 L55 71 L77 47"
                                fill="none"
                                stroke="#D4B876"
                                strokeWidth="5.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lg-seal-check"
                            />
                        </svg>
                    </div>

                    <div className="lg-header-title">ورود به سامانه</div>

                    <div className="lg-badge">
                        <AuditOutlined /> دسترسی امن و رمزنگاری‌شده
                    </div>
                </div>

                <form className="lg-body" onSubmit={handleSubmit} noValidate>
                    <div className="lg-field-group">
                        <label className="lg-field-label" htmlFor="lg-email">
                            ایمیل
                        </label>
                        <div className={`lg-field-wrap ${errors.email ? "lg-field-wrap--error" : ""}`}>
                            <MailOutlined className="lg-field-icon" />
                            <input
                                id="lg-email"
                                type="email"
                                className="lg-input"
                                placeholder="example@domain.com"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (errors.email) setErrors((p) => ({ ...p, email: undefined }));
                                }}
                                autoComplete="username"
                            />
                        </div>
                        {errors.email && (
                            <span className="lg-error-text">
                                <ExclamationCircleOutlined /> {errors.email}
                            </span>
                        )}
                    </div>

                    <div className="lg-field-group">
                        <label className="lg-field-label" htmlFor="lg-password">
                            رمز عبور
                        </label>
                        <div className={`lg-field-wrap ${errors.password ? "lg-field-wrap--error" : ""}`}>
                            <LockOutlined className="lg-field-icon" />
                            <input
                                id="lg-password"
                                type={showPassword ? "text" : "password"}
                                className="lg-input lg-input--password"
                                placeholder="رمز عبور خود را وارد کنید"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (errors.password) setErrors((p) => ({ ...p, password: undefined }));
                                }}
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="lg-eye-btn"
                                onClick={() => setShowPassword((p) => !p)}
                                tabIndex={-1}
                                aria-label={showPassword ? "پنهان کردن رمز عبور" : "نمایش رمز عبور"}
                            >
                                {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                            </button>
                        </div>
                        {errors.password && (
                            <span className="lg-error-text">
                                <ExclamationCircleOutlined /> {errors.password}
                            </span>
                        )}
                    </div>

                    <button type="submit" className={`lg-submit ${loading ? "lg-submit--loading" : ""}`} disabled={loading}>
                        {loading ? (
                            <>
                                <span className="lg-spinner" /> در حال ورود...
                            </>
                        ) : (
                            <>
                                <LoginOutlined /> ورود به سامانه
                            </>
                        )}
                    </button>
                </form>

                <div className="lg-tally" aria-hidden="true">
                    <span className="lg-tally-group">
                        <i></i><i></i><i></i><i></i><i className="lg-tally-slash"></i>
                    </span>
                    <span className="lg-tally-group">
                        <i></i><i></i><i></i><i></i><i className="lg-tally-slash"></i>
                    </span>
                </div>

                <div className="lg-footer">
                    <SafetyCertificateOutlined /> فقط کاربران مجاز امکان ورود به این پنل را دارند
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700');

                .lg-root {
                    --ledger: #0E1A16;
                    --ledger-2: #142722;
                    --paper: #FBF9F4;
                    --ink: #1C2321;
                    --ink-soft: #5B6663;
                    --gold: #B08D46;
                    --gold-light: #D4B876;
                    --gold-deep: #8B6A2E;
                    --teal: #1F6F63;
                    --teal-deep: #123832;
                    --clay: #A6453B;
                    --clay-bg: #F7E9E7;

                    min-height: 100vh;
                    display: flex; align-items: center; justify-content: center;
                    padding: 24px;
                    position: relative;
                    overflow: hidden;
                    direction: rtl;
                    background:
                        radial-gradient(circle at 26% 18%, rgba(212,184,118,0.12), transparent 55%),
                        radial-gradient(circle at 82% 88%, rgba(31,111,99,0.18), transparent 50%),
                        linear-gradient(180deg, var(--ledger) 0%, var(--ledger-2) 100%);
                }

                .lg-root::before {
                    content: "";
                    position: absolute; inset: -20%;
                    background-image:
                        linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);
                    background-size: 30px 30px;
                    animation: lg-grid-drift 70s linear infinite;
                    z-index: 0;
                }

                @keyframes lg-grid-drift {
                    from { transform: translate(0, 0); }
                    to   { transform: translate(30px, 30px); }
                }

                .lg-card {
                    position: relative; z-index: 1;
                    width: 100%; max-width: 420px;
                    background: var(--paper);
                    border-radius: 20px;
                    box-shadow: 0 32px 80px rgba(6,12,10,0.35), 0 0 0 1px rgba(176,141,70,0.15);
                    overflow: hidden;
                }

                .lg-card::before {
                    content: "";
                    display: block;
                    height: 4px;
                    background: linear-gradient(90deg, var(--gold) 0%, var(--gold-light) 50%, var(--teal) 100%);
                }

                .lg-anim-rise { animation: lg-rise .55s cubic-bezier(.4,0,.2,1); }
                @keyframes lg-rise {
                    from { opacity: 0; transform: translateY(28px) scale(.97); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }

                .lg-header {
                    background: linear-gradient(160deg, var(--ledger-2) 0%, var(--ledger) 100%);
                    padding: 34px 32px 26px;
                    display: flex; flex-direction: column; align-items: center;
                    text-align: center;
                    gap: 6px;
                    position: relative;
                }

                .lg-seal { width: 76px; height: 76px; margin-bottom: 8px; }
                .lg-seal-svg { width: 100%; height: 100%; }
                .lg-seal-check {
                    stroke-dasharray: 46;
                    stroke-dashoffset: 46;
                    animation: lg-seal-draw .5s ease-out .55s forwards;
                }
                .lg-seal-svg circle, .lg-seal-ticks {
                    transform-origin: 60px 60px;
                }
                .lg-seal {
                    animation: lg-stamp .6s cubic-bezier(.34,1.56,.64,1) both;
                }
                @keyframes lg-stamp {
                    0%   { opacity: 0; transform: scale(1.5) rotate(-10deg); }
                    60%  { opacity: 1; transform: scale(0.94) rotate(2deg); }
                    100% { opacity: 1; transform: scale(1) rotate(0deg); }
                }
                @keyframes lg-seal-draw {
                    to { stroke-dashoffset: 0; }
                }

                .lg-header-title {
                    font-family: 'Noto Nastaliq Urdu', serif;
                    font-size: 25px; font-weight: 700; color: #fff;
                    line-height: 1.9;
                }
                .lg-header-sub { font-size: 13px; color: rgba(255,255,255,0.72); line-height: 1.8; max-width: 280px; }

                .lg-badge {
                    margin-top: 10px;
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 5px 12px;
                    border-radius: 999px;
                    background: rgba(212,184,118,0.14);
                    border: 1px solid rgba(212,184,118,0.4);
                    color: var(--gold-light);
                    font-size: 11.5px; font-weight: 600;
                    letter-spacing: 0.2px;
                }

                .lg-body {
                    padding: 30px 32px 6px;
                    display: flex; flex-direction: column; gap: 20px;
                }

                .lg-field-group { display: flex; flex-direction: column; gap: 8px; }

                .lg-field-label { font-size: 13.5px; font-weight: 700; color: var(--ink); }

                .lg-field-wrap {
                    position: relative;
                    display: flex; align-items: center;
                }

                .lg-field-icon {
                    position: absolute; right: 16px;
                    font-size: 16px; color: var(--ink-soft);
                    pointer-events: none;
                    transition: color .2s;
                }

                .lg-input {
                    width: 100%; box-sizing: border-box;
                    padding: 13px 44px 13px 16px;
                    border: 1.5px solid #E4DFD1;
                    border-radius: 12px;
                    font-size: 14px; color: var(--ink);
                    background: #FDFCF9;
                    outline: none;
                    transition: all .2s;
                    direction: ltr;
                    text-align: right;
                }
                .lg-input--password { padding-left: 44px; }

                .lg-input::placeholder { color: #A6A093; }

                .lg-input:focus {
                    border-color: var(--teal);
                    background: #fff;
                    box-shadow: 0 0 0 3px rgba(31,111,99,.14);
                }
                .lg-input:focus ~ .lg-field-icon { color: var(--teal); }

                .lg-field-wrap--error .lg-input {
                    border-color: #D9A8A3;
                    background: var(--clay-bg);
                }
                .lg-field-wrap--error .lg-field-icon { color: var(--clay); }

                .lg-eye-btn {
                    position: absolute; left: 14px;
                    background: none; border: none;
                    font-size: 16px; color: var(--ink-soft);
                    cursor: pointer; padding: 4px;
                    display: flex; align-items: center;
                    transition: color .2s;
                }
                .lg-eye-btn:hover { color: var(--teal); }
                .lg-eye-btn:focus-visible { outline: 2px solid var(--teal); outline-offset: 2px; border-radius: 6px; }

                .lg-error-text {
                    display: flex; align-items: center; gap: 6px;
                    font-size: 12px; font-weight: 600; color: var(--clay);
                    animation: lg-rise .25s ease;
                }

                .lg-submit {
                    margin-top: 4px;
                    display: flex; align-items: center; justify-content: center; gap: 10px;
                    padding: 14px 20px;
                    border: none; border-radius: 12px;
                    border-top: 2px solid var(--gold-light);
                    background: linear-gradient(135deg, var(--teal) 0%, var(--teal-deep) 100%);
                    color: #fff; font-size: 15px; font-weight: 800;
                    cursor: pointer;
                    box-shadow: 0 6px 20px rgba(18,56,50,.35);
                    transition: all .25s cubic-bezier(.4,0,.2,1);
                }
                .lg-submit:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 28px rgba(18,56,50,.45);
                }
                .lg-submit:focus-visible { outline: 2px solid var(--gold-light); outline-offset: 2px; }
                .lg-submit:disabled { cursor: not-allowed; opacity: .85; }

                .lg-spinner {
                    width: 16px; height: 16px;
                    border: 2.5px solid rgba(255,255,255,0.3);
                    border-top-color: var(--gold-light);
                    border-radius: 50%;
                    animation: lg-spin .7s linear infinite;
                }
                @keyframes lg-spin { to { transform: rotate(360deg); } }

                .lg-tally {
                    display: flex; align-items: center; justify-content: center; gap: 18px;
                    padding: 22px 0 6px;
                }
                .lg-tally-group {
                    position: relative;
                    display: inline-flex; align-items: flex-end; gap: 3px;
                }
                .lg-tally-group i {
                    display: block;
                    width: 2px; height: 14px;
                    background: var(--gold);
                    opacity: 0.4;
                    border-radius: 1px;
                }
                .lg-tally-group i.lg-tally-slash {
                    position: absolute;
                    left: -1px; top: 1px;
                    width: 16px; height: 2px;
                    transform: rotate(-52deg);
                    transform-origin: left center;
                }

                .lg-footer {
                    display: flex; align-items: center; justify-content: center; gap: 7px;
                    padding: 4px 32px 26px;
                    font-size: 12px; color: var(--ink-soft); font-weight: 600;
                    text-align: center;
                }

                @media (max-width: 420px) {
                    .lg-header { padding: 28px 22px 22px; }
                    .lg-body { padding: 26px 22px 4px; }
                    .lg-footer { padding: 4px 22px 22px; }
                }

                @media (prefers-reduced-motion: reduce) {
                    .lg-anim-rise, .lg-seal, .lg-seal-check, .lg-root::before, .lg-error-text {
                        animation: none !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default LoginPage;